import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../jscontrol/apiClient';
import { getImageSrc } from '../../utils/appConfig';
import { uploadImageFiles } from '../utils/imageUpload';

const ProductImageManage = ({ productId }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [toast, setToast] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ IsPrimary: false, DisplayOrder: 0 });
  const [previewUrl, setPreviewUrl] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const toastTimer = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };

  const fetchImages = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiFetch(`/ProductImage/GetByProductId/${productId}`);
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setImages(list);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const openAddForm = () => {
    setEditItem(null);
    setForm({ IsPrimary: false, DisplayOrder: images.length + 1 });
    setPreviewUrl('');
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowForm(true);
  };

  const openEditForm = (img) => {
    setEditItem(img);
    setForm({
      IsPrimary: img.IsPrimary || false,
      DisplayOrder: img.DisplayOrder || 0,
    });
    setPreviewUrl(getImageSrc(img.ImageUrl));
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditItem(null);
    setPreviewUrl('');
    setSelectedFiles([]);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setSelectedFiles(files);
    setPreviewUrl(URL.createObjectURL(files[0]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editItem && selectedFiles.length === 0) { showToast('Please select image(s).', 'danger'); return; }
    setSaving(true);
    try {
      let imageUrl = editItem?.ImageUrl || '';

      if (selectedFiles.length > 0) {
        setUploading(true);
        const urls = await uploadImageFiles('/ProductImage', selectedFiles);
        setUploading(false);

        if (editItem) {
          imageUrl = urls[0];
          await apiFetch('/ProductImage/Update', {
            method: 'PUT',
            data: {
              ProductImageId: editItem.ProductImageId,
              ImageUrl: imageUrl,
              IsPrimary: form.IsPrimary,
              DisplayOrder: Number(form.DisplayOrder) || 0,
              UpdatedBy: 0,
            },
          });
          showToast('Image updated successfully.');
        } else {
          let order = Number(form.DisplayOrder) || images.length + 1;
          for (let i = 0; i < urls.length; i++) {
            await apiFetch('/ProductImage/Create', {
              method: 'POST',
              data: {
                ProductId: Number(productId),
                ImageUrl: urls[i],
                IsPrimary: form.IsPrimary && i === 0,
                DisplayOrder: order + i,
                CreatedBy: 0,
              },
            });
          }
          showToast(`${urls.length} image(s) added successfully.`);
        }
      } else if (editItem) {
        await apiFetch('/ProductImage/Update', {
          method: 'PUT',
          data: {
            ProductImageId: editItem.ProductImageId,
            ImageUrl: imageUrl,
            IsPrimary: form.IsPrimary,
            DisplayOrder: Number(form.DisplayOrder) || 0,
            UpdatedBy: 0,
          },
        });
        showToast('Image updated successfully.');
      }
      closeForm();
      fetchImages();
    } catch (e) {
      setUploading(false);
      showToast(e.message, 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (img) => {
    if (!window.confirm('Delete this image?')) return;
    setDeleting(img.ProductImageId);
    try {
      await apiFetch(`/ProductImage/Delete/${img.ProductImageId}`, { method: 'DELETE' });
      showToast('Image deleted.');
      fetchImages();
    } catch (e) {
      showToast(e.message, 'danger');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Toast */}
      {toast && (
        <div
          className={`alert alert-${toast.type} py-2 px-3`}
          style={{ fontSize: '0.83rem', position: 'absolute', top: 0, right: 0, zIndex: 10, minWidth: 220 }}
        >
          {toast.msg}
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0 fw-semibold">Product Images</h6>
        <button className="btn btn-primary btn-sm" onClick={openAddForm}>
          <i className="mdi mdi-plus me-1"></i>Add Image
        </button>
      </div>

      {/* Add / Edit Form */}
      {showForm && (
        <div className="card mb-4 border" style={{ background: '#f8f9fa' }}>
          <div className="card-body py-3">
            <h6 className="mb-3">{editItem ? 'Edit Image' : 'Add New Image'}</h6>
            <form onSubmit={handleSubmit}>
              <div className="form-group mb-3">
                <label className="form-label">
                  Select Image(s) <span className="text-danger">*</span>
                  {editItem && <span className="text-muted ms-2" style={{ fontSize: '0.78rem' }}>(leave blank to keep existing)</span>}
                </label>

                {/* Clickable upload area */}
                <div
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  style={{
                    border: '2px dashed #6f42c1',
                    borderRadius: 8,
                    padding: '24px 16px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: '#faf8ff',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0eaff'}
                  onMouseLeave={e => e.currentTarget.style.background = '#faf8ff'}
                >
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="preview"
                      style={{ maxHeight: 140, maxWidth: '100%', objectFit: 'contain', borderRadius: 4 }}
                    />
                  ) : (
                    <>
                      <i className="mdi mdi-cloud-upload" style={{ fontSize: '2rem', color: '#6f42c1' }}></i>
                      <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#6c757d' }}>
                        Click to select image(s)
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#adb5bd' }}>JPG, PNG, WEBP — multiple allowed</div>
                    </>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />

                {selectedFiles.length > 1 && (
                  <div className="text-muted mt-1" style={{ fontSize: '0.78rem' }}>
                    {selectedFiles.length} files selected
                  </div>
                )}

                {previewUrl && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-danger p-0 mt-1"
                    onClick={() => {
                      setPreviewUrl(editItem ? getImageSrc(editItem.ImageUrl) : '');
                      setSelectedFiles([]);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  >
                    <i className="mdi mdi-close me-1"></i>
                    {editItem ? 'Revert to original' : 'Remove'}
                  </button>
                )}
              </div>

              <div className="row mb-3">
                <div className="col-6">
                  <label className="form-label">Display Order</label>
                  <input
                    className="form-control"
                    type="number"
                    name="DisplayOrder"
                    value={form.DisplayOrder}
                    onChange={handleChange}
                    min={0}
                  />
                </div>
                <div className="col-6 d-flex align-items-end pb-1">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="IsPrimary"
                      id="img_isPrimary"
                      checked={form.IsPrimary}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="img_isPrimary">Set as Primary</label>
                  </div>
                </div>
              </div>

              <div className="d-flex gap-2">
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving || uploading}>
                  {uploading
                    ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Uploading...</>
                    : saving
                    ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Saving...</>
                    : editItem ? 'Update' : 'Save'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={closeForm}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image List */}
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger py-2" style={{ fontSize: '0.85rem' }}>
          <i className="mdi mdi-alert-circle me-1"></i>{error}
        </div>
      ) : images.length === 0 ? (
        <div className="text-center text-muted py-5" style={{ fontSize: '0.9rem' }}>
          <i className="mdi mdi-image-off" style={{ fontSize: '2rem', display: 'block', marginBottom: 8 }}></i>
          No images yet. Click "Add Image" to upload one.
        </div>
      ) : (
        <div className="row g-3">
          {images.map(img => (
            <div className="col-6 col-sm-4 col-md-3" key={img.ProductImageId}>
              <div
                className="card h-100 text-center"
                style={{
                  border: img.IsPrimary ? '2px solid #6f42c1' : '1px solid #dee2e6',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <div style={{ background: '#f8f9fa', height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
                  <img
                    src={getImageSrc(img.ImageUrl)}
                    alt={`product-img-${img.ProductImageId}`}
                    style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div className="card-body py-2 px-2">
                  <div className="d-flex align-items-center justify-content-center gap-2 mb-1">
                    <div
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        border: img.IsPrimary ? '2px solid #6f42c1' : '2px solid #ced4da',
                        background: img.IsPrimary ? '#6f42c1' : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {img.IsPrimary && (
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: img.IsPrimary ? '#6f42c1' : '#adb5bd', fontWeight: img.IsPrimary ? 600 : 400 }}>
                      Primary
                    </span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>Order: {img.DisplayOrder}</div>
                  <div className="d-flex gap-1 justify-content-center mt-2">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      style={{ fontSize: '0.73rem', padding: '2px 8px' }}
                      onClick={() => openEditForm(img)}
                    >
                      <i className="mdi mdi-pencil"></i>
                    </button>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      style={{ fontSize: '0.73rem', padding: '2px 8px' }}
                      onClick={() => handleDelete(img)}
                      disabled={deleting === img.ProductImageId}
                    >
                      {deleting === img.ProductImageId
                        ? <span className="spinner-border spinner-border-sm" role="status"></span>
                        : <i className="mdi mdi-delete"></i>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductImageManage;
